import PageLayout from '../../wrappers/PageLayout';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/Loader/Loader';
import LabelSwitch from '@/components/LabelSwitch/LabelSwitch';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { SellerDataUnit } from '@/components/SellerDataUnit/SellerDataUnit';
import { APIEventsEnum, useAppSelector, useSendEvent } from '../../hooks';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDataUnits,
  getDataUnitsStats,
  getRegisteredVATIds,
  getUploadedDocumentsFiles,
  postDiscardFile,
} from '@/core/api';
import { SellerManualFiles } from '@/components/SellerManualFiles/SellerManualFiles';
import FileUploader from '@/components/FileUploader/FileUploader';
import Button from '@/components/Button';
import UploadIcon from '@/icons/upload_arrow.svg?react';
import ManualUploadFile from '@/components/ManualDataUploader/ManualUploadFile';
import SendIcon from '@/icons/sent.svg?react';
import { EmptyStateTile } from '@/components/EmptyStateTile';
import { ACCEPTED_EXTS } from '@/components/ManualDataUploader/ManualUploader.helpers';
import { useMU } from '../../context/useMU';
import { useEffect, useMemo, useState } from 'react';
import { DataUnitStatsItem } from '@/core/types';
import { useModal } from '@/components/Modal/useModal';
import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import useNotification from '@/hooks/useNotification';
import FeedbackWidget from '@/components/FeedbackWidget/FeedbackWidget';
import { useConfirmDataUnitModal } from '@/components/ConfirmDataUnitModal/useConfirmDataUnitModal';
import { ConfirmDataUnitModal } from '@/components/ConfirmDataUnitModal/ConfirmDataUnitModal';
import { AuthedRoutesEnum, PlaceToUploadEnum } from '@/core/enums';
import { getDocsLinkQueryParams } from '@/pages/Seller/SellerTurnover.helpers';
import { normalizeDateFE } from '@/core/normalization';
import { differenceInCalendarMonths, endOfMonth, format } from 'date-fns';
import { useWSMessageHandler } from '@/core/ws/useWSMessageHandler';
import { isUploadedDocumentsFileProgressMessage } from '@/core/ws/ws-messages';
import { Spinner } from '@/components/Spinner';
import { formatDate } from '@/core/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SellerDataUnitOld } from '@/components/SellerDataUnit/SellerDataUnitOld';
import SellerGuidelines from '@/components/SellerGuidelines';

type SwitchLabelType = 'All imports' | 'Manually uploaded';

export const SellerTurnover = () => {
  const { vs6405CpDataProvidingStepsInCurrentDu } = useFlags();
  const { sendEvent } = useSendEvent();
  const [currSliceNum, setCurrSliceNum] = useState(1);

  const [isUpdatingStats, setIsUpdatingStats] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fileIdForDeleting, setFileIdForDeleting] = useState<number>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const activeCompany = useAppSelector(activeCompanySelector);
  const queryKey = useAppSelector(queryKeySelector);
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const {
    openModal: openConfirmDataUnitModal,
    setActiveDU,
    setActiveDuStats,
    activeDUDate,
    closeModal: closeConfirmDataUnitModal,
  } = useConfirmDataUnitModal();

  const filesTabParam = searchParams.get('files');
  const isFilesTab = filesTabParam === '1';

  const { t, i18n } = useTranslation();

  const {
    onSubmitFiles,
    onSelectFiles,
    onDeleteFileToUpload,
    uploadedFiles,
    filesToUpload,
    isUploadingFiles,
    isLoadingUploadedFiles,
    placeToUpload,
  } = useMU();

  const { data: dataUnits, isLoading: isLoadingDataUnits } = useQuery({
    queryKey: ['SellerDataUnits', queryKey],
    queryFn: () => getDataUnits(activeCompany?.company_id!),
  });

  const { data: registeredVATIds, isLoading: isLoadingRegisteredVATIds } =
    useQuery({
      queryKey: ['SellerRegisteredVATIds', queryKey],
      queryFn: () => getRegisteredVATIds(activeCompany?.company_id),
    });

  const hasDeEsReports = registeredVATIds?.some(
    (item) =>
      item.Country.short_name === 'DE' || item.Country.short_name === 'ES'
  );

  const currSlice = useMemo(() => {
    return dataUnits?.slice(0, currSliceNum * 3);
  }, [dataUnits, currSliceNum]);

  const totalSlices = Math.ceil((dataUnits?.length || 0) / 3);

  const { from, to } = useMemo(() => {
    const to = currSlice?.[0]?.date;
    const from = currSlice?.[currSlice.length - 1]?.date;
    return {
      from: from ? format(normalizeDateFE(from), 'yyyy-MM-dd') : undefined,
      to: to
        ? format(endOfMonth(normalizeDateFE(to)), 'yyyy-MM-dd')
        : undefined,
    };
  }, [currSlice]);

  const { data: dataUnitsStats, isLoading: isLoadingDataUntisStats } = useQuery(
    {
      queryKey: ['SellerTurnoverDUStats', queryKey],
      queryFn: () => getDataUnitsStats(activeCompany?.company_id, from, to),
      enabled: !!from && !!to && !!activeCompany,
    }
  );
  const currDU = useMemo(() => {
    return dataUnits?.find(
      (du) =>
        differenceInCalendarMonths(new Date(), normalizeDateFE(du.date)) === 1
    );
  }, [dataUnits]);

  const activeCompanyId = activeCompany?.company_id;
  useWSMessageHandler(
    (msg) => {
      if (
        isUploadedDocumentsFileProgressMessage(msg) &&
        activeCompanyId === msg.data.companyId
      ) {
        if (
          msg.data.type === 'refresh-defined-fields' &&
          msg.data.status === 'finished'
        ) {
          const newFrom = currSlice?.[currSlice.length - 1]?.date;
          const newTo = currSlice?.[0]?.date;

          setIsUpdatingStats(true);

          queryClient.invalidateQueries({
            queryKey: ['DUSourceTotals', currDU?.id, activeCompanyId],
          });

          getDataUnitsStats(
            activeCompanyId,
            newFrom ? format(normalizeDateFE(newFrom), 'yyyy-MM-dd') : from,
            newTo ? format(normalizeDateFE(newTo), 'yyyy-MM-dd') : to
          )
            .then((res) => {
              queryClient.setQueryData(
                ['SellerTurnoverDUStats', queryKey],
                res
              );
              setIsUpdatingStats(false);
            })
            .catch(() => {
              setIsUpdatingStats(false);
            });
        }
      }
    },
    // eslint-disable-next-line
    [from, to, activeCompanyId, currSlice, currDU]
  );

  const {
    isModalOpened: isConfirmDeleteFileModalOpened,
    closeModal: onCloseConfirmDeleteFileModal,
    openModal: openConfirmDeleteFileModal,
  } = useModal();

  useEffect(() => {
    sendEvent(APIEventsEnum.view_new_turnover_section, {
      block: 'customer opened the new turnover section',
    });
    // eslint-disable-next-line
  }, []);

  const onClickDeleteFile = (id: number) => {
    setFileIdForDeleting(id);
    openConfirmDeleteFileModal();
  };

  const DUStats = useMemo(() => {
    return dataUnitsStats?.reduce(
      (acc: { [key: number]: DataUnitStatsItem }, s: DataUnitStatsItem) => {
        acc[s.data_unit_id] = s;
        return acc;
      },
      {}
    );
  }, [dataUnitsStats]);

  const renderContent = () => {
    if (
      isLoadingUploadedFiles ||
      isLoadingDataUnits ||
      isLoadingDataUntisStats ||
      isLoadingRegisteredVATIds
    )
      return null;

    if (
      (!isFilesTab && !dataUnits?.length) ||
      (isFilesTab && !uploadedFiles?.length)
    ) {
      return (
        <EmptyStateTile
          text="Wait until documents will be imported from the data sources or upload the files manually if you don't have integrations"
          title='No data imported yet'
        />
      );
    }

    if (isFilesTab) {
      return (
        <SellerManualFiles
          uploadedFiles={uploadedFiles}
          onClickDeleteFile={onClickDeleteFile}
        />
      );
    }

    return (
      <>
        {(currSlice || []).map((u) => {
          const currStats = DUStats?.[u.id];
          return vs6405CpDataProvidingStepsInCurrentDu ? (
            <SellerDataUnit
              onClickDeleteFile={onClickDeleteFile}
              key={u.id}
              unit={u}
              stats={currStats}
              onConfirm={() => {
                setActiveDU(u);
                setActiveDuStats(currStats);
                openConfirmDataUnitModal();
              }}
              isUploadingFiles={isUploadingFiles}
              hasDeEsReports={hasDeEsReports}
            />
          ) : (
            <SellerDataUnitOld
              onClickDeleteFile={onClickDeleteFile}
              key={u.id}
              unit={u}
              stats={currStats}
              onConfirm={() => {
                setActiveDU(u);
                setActiveDuStats(currStats);
                openConfirmDataUnitModal();
              }}
              isUploadingFiles={isUploadingFiles}
              hasDeEsReports={hasDeEsReports}
            />
          );
        })}
        {totalSlices > 0 && currSliceNum < totalSlices && (
          <Button
            data-testid='show_previous_periods_btn'
            className='flex !w-full items-center justify-center gap-x-1'
            styling='outline'
            onClick={() => {
              const newSlice = dataUnits?.slice(
                currSliceNum * 3,
                (currSliceNum + 1) * 3
              );
              const newFrom = newSlice?.[newSlice.length - 1]?.date;
              const newTo = newSlice?.[0]?.date;
              if (!newFrom || !newTo) return;

              setIsLoadingMore(true);
              getDataUnitsStats(
                activeCompany?.company_id,
                format(normalizeDateFE(newFrom), 'yyyy-MM-dd'),
                format(normalizeDateFE(newTo), 'yyyy-MM-dd')
              )
                .then((res) => {
                  queryClient.setQueryData(
                    ['SellerTurnoverDUStats', queryKey],
                    [...dataUnitsStats!, ...(res || [])]
                  );
                  setCurrSliceNum((prev) => prev + 1);
                  setIsLoadingMore(false);
                })
                .catch(() => {
                  setIsLoadingMore(false);
                });
            }}
            disabled={isLoadingMore}
          >
            {isLoadingMore && <Spinner />}
            <span>{t('Show previous periods')}</span>
          </Button>
        )}
      </>
    );
  };

  const isProcessingFiles =
    isUploadingFiles ||
    uploadedFiles.filter((f) => !!f.update_progress).length > 0;

  return (
    <PageLayout
      className='flex-grow'
      rightColumn={
        <div style={{ maxWidth: 328 }} className='pt-4 lg:ml-auto lg:pt-0'>
          <FeedbackWidget testId='FeedbackWidget' />
          <SellerGuidelines />
        </div>
      }
    >
      <div>
        {(isLoadingUploadedFiles ||
          isLoadingDataUnits ||
          isLoadingDataUntisStats ||
          isUpdatingStats ||
          isLoadingRegisteredVATIds) && <Loader />}
        <div className='mb-6 space-y-4'>
          <div className='rounded-lg bg-white pb-4 pl-4 pr-7 pt-2'>
            <div className='mb-2'>
              <div className='flex items-center justify-between'>
                <div className='text-h4 font-extrabold'>
                  {t('Upload turnover data')}
                </div>
                <div className='flex items-center gap-x-4'>
                  {isProcessingFiles ? (
                    <Button
                      disabled
                      className='Button flex !w-[140px] cursor-pointer items-center !pl-3'
                    >
                      <UploadIcon className='mr-2 [&>path]:fill-white' />
                      <span>{t('Upload file')}</span>
                    </Button>
                  ) : (
                    <FileUploader
                      onSelect={onSelectFiles()}
                      name='manual-upload-file'
                      accept={ACCEPTED_EXTS}
                      className='Button flex !w-[140px] cursor-pointer items-center !pl-3'
                      multiple={false}
                    >
                      <UploadIcon className='mr-2 [&>path]:fill-white' />
                      <span>{t('Upload file')}</span>
                    </FileUploader>
                  )}
                </div>
              </div>
              <div>
                <p>
                  {t(
                    'Please upload turnover data files or files with corrections.'
                  )}
                </p>
                <p>
                  {t(
                    'Your file will be processed and made available within the designated period.'
                  )}
                </p>
              </div>
            </div>
            <form
              onSubmit={(e) => {
                sendEvent(
                  APIEventsEnum.files_uploaded_from_new_turnover_section,
                  {
                    block:
                      'customer uploaded files from the new turnover section',
                  }
                );
                onSubmitFiles(e);
              }}
            >
              {!!filesToUpload.length &&
                placeToUpload === PlaceToUploadEnum.Main && (
                  <div className='pt-2'>
                    {filesToUpload.map((f) => (
                      <ManualUploadFile
                        className='mb-2 flex-grow'
                        onDeleteFile={onDeleteFileToUpload(f.name)}
                        file={f}
                        disabled={false}
                        key={f.name}
                        isUploading={isUploadingFiles}
                      />
                    ))}
                  </div>
                )}
              {!!filesToUpload.length &&
                placeToUpload === PlaceToUploadEnum.Main &&
                !isUploadingFiles && (
                  <div>
                    <Button
                      className='mt-4 flex items-center !py-1 !pl-4 !pr-6'
                      type='submit'
                      disabled={isUploadingFiles}
                      data-testid='manual-upload-submit'
                    >
                      <SendIcon className='mr-2' />
                      <span>{t('Submit file')}</span>
                    </Button>
                  </div>
                )}
            </form>
          </div>
        </div>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-h4 font-black'>{t('Turnover data')}</span>
          <LabelSwitch<SwitchLabelType>
            activeItem={filesTabParam ? 'Manually uploaded' : 'All imports'}
            onClick={(v) => () => {
              if (v === 'All imports') {
                searchParams.delete('files');
              } else {
                searchParams.set('files', '1');
              }
              setSearchParams(searchParams, {
                state: location.state,
              });
            }}
            className={`font-semibold ${
              i18n.language === 'de' ? '!w-[355px]' : '!w-[320px]'
            }`}
            items={[
              { value: 'All imports', label: t('All imports') },
              { value: 'Manually uploaded', label: t('Manually uploaded') },
            ]}
          />
        </div>
        <div className='space-y-4'>{renderContent()}</div>
      </div>
      <ConfirmModal
        danger
        isModalOpened={isConfirmDeleteFileModalOpened}
        closeModal={onCloseConfirmDeleteFileModal}
        confirm={(setIsLoading) => {
          if (!fileIdForDeleting || !activeCompany) return;
          setIsLoading(true);
          postDiscardFile(fileIdForDeleting, activeCompany?.company_id)
            .then(() =>
              Promise.all([
                getDataUnits(activeCompany.company_id),
                getUploadedDocumentsFiles(activeCompany.company_id),
                getDataUnitsStats(activeCompany.company_id, from, to),
              ])
            )
            .then(([DURes, filesRes, DUStatsRes]) => {
              queryClient.invalidateQueries({
                queryKey: ['DUSourceTotals', currDU?.id, activeCompanyId],
              });
              queryClient.setQueryData(['SellerDataUnits', queryKey], DURes);
              queryClient.setQueryData(
                ['SellerManualUploadFiles', queryKey],
                filesRes
              );
              queryClient.setQueryData(
                ['SellerTurnoverDUStats', queryKey],
                DUStatsRes
              );
              setIsLoading(false);
              onCloseConfirmDeleteFileModal();
              setFileIdForDeleting(undefined);
              showNotification({
                text: 'The file has been deleted',
                type: 'success',
              });
            })
            .catch((err) => {
              console.error(err);
              setIsLoading(false);
              showNotification({
                text: 'This file has been processed, so it cannot be deleted.',
                type: 'error',
              });
            });
        }}
        confirmBtnText='Confirm and delete'
        title='Deleting file'
      >
        <div className='mb-2 text-h4 font-extrabold'>
          {t('Confirm deleting this file')}
        </div>
        <div>
          {t('It will no longer be available for processing by countX')}
        </div>
      </ConfirmModal>
      <ConfirmDataUnitModal>
        {activeDUDate && (
          <Link
            onClick={closeConfirmDataUnitModal}
            to={{
              pathname: AuthedRoutesEnum.Documents,
              search: getDocsLinkQueryParams(
                activeDUDate.getMonth(),
                activeDUDate.getFullYear(),
                activeCompany?.company_id
              ),
            }}
            className='Button--outline Button--sm inline-flex !font-semibold'
          >
            {t('Open *month* documents').replace(
              '*month*',
              formatDate(activeDUDate, 'MMMM')
            )}
          </Link>
        )}
      </ConfirmDataUnitModal>
    </PageLayout>
  );
};
